import {Injectable} from '@angular/core';
import {
  AdvancedFilterGroup,
  AnalyticOperation,
  AnalyticOperationData,
  CheckboxFilterItem,
  EventType,
  FilterGroup,
  GeneralFilter,
  GetAdvancedParams,
  GetResponse,
  InvoiceOrder,
  PaginationData,
  Partner,
  Project,
  PurchaseOrder,
  Service,
  ServiceOrder,
  ServiceType
} from '@app/shared';
import {BehaviorSubject, finalize, firstValueFrom, forkJoin, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {StServiceService} from '@app/core';
import {ProjectUtilsService} from '../../project/services/project-utils.service';
import {GlobalValuesService} from '../../../core/services/global-values.service';
import {AdministrationFilterService, AdministrationFilterResponse, AdministrationFilterOption} from './administration-filter.service';

type ServiceOrderSortDirection = 'asc' | 'desc';

const SEARCH_FILTER_FIELDS: string[] = [
  'Reference',
  'ReferenceCommercial',
  'ReferenceCEC',
  'ProjectContainer.ContainerRef',
  'MBL'
];

const GENERAL_FILTER_LABELS = {
  dateRange: ' - ',
  withoutInvoice: 'Sin Estado de pago',
  withoutPurchaseOrder: 'Sin Orden de compra',
};

@Injectable({
  providedIn: 'root'
})
export class AdministrationUtilsService {

  /** Variables principales **/
  totalServiceOrders: ServiceOrder[] = [];
  filteredServiceOrders$ = new BehaviorSubject<ServiceOrder[]>([]);
  administrationView$ = new BehaviorSubject<'Ver órdenes' | 'Ver estados de pago' | 'Ver órdenes de compra'>('Ver órdenes');

  private readonly defaultSortProperty = 'Reference';
  private readonly defaultSortDirection: ServiceOrderSortDirection = 'desc';

  private currentSortProperty: string = this.defaultSortProperty;
  private currentSortDirection: ServiceOrderSortDirection = this.defaultSortDirection;
  private currentFilterGroups: FilterGroup[][] = [];
  private currentPage = 1;
  private filtersInitPromise?: Promise<void>;
  private suppressFilterTrigger = false;

  /** Variables auxiliares **/
  tableLoading$ = new BehaviorSubject<boolean>(false);

  /** Variables de logica de paginacion y filtrado **/
  paginationData$ = new BehaviorSubject<PaginationData>({
    startRow: 0,
    endRow: 0,
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
  });

  filters: AdvancedFilterGroup[] = [];

  activeFilters$ = new BehaviorSubject<number>(0);

  generalFilters: GeneralFilter[] = [
    {
      name: '15 MAR - 27 MAR',
      icon: 'calendar_today',
      active: false,
      value: {startDate: '2023-03-15', endDate: '2023-03-27'},
    },
    {
      name: 'Sin Estado de pago',
      icon: 'receipt_long_off',
      active: false,
    },
    {
      name: 'Sin Orden de compra',
      icon: 'shopping_cart_off',
      active: false,
    },
    {
      name: 'Pendientes de facturar',
      icon: 'pending_actions',
      active: false,
    },
    {
      name: 'Proyecto',
      icon: 'business',
      active: false,
      type: 'select',
      options: [],
    },
  ];
  searchValue: string = '';

  constructor(
    private stServiceService: StServiceService,
    private ps: ProjectUtilsService,
    private gs: GlobalValuesService,
    private administrationFilterService: AdministrationFilterService,
  ) {
    this.resetFilter();
  }

  private async ensureFiltersInitialized(): Promise<void> {
    if (!this.filtersInitPromise) {
      this.filtersInitPromise = this.loadAdvancedFilters();
    }
    return this.filtersInitPromise;
  }

  private async loadAdvancedFilters(): Promise<void> {
    try {
      const responses = await firstValueFrom(
        forkJoin({
          states: this.administrationFilterService.getStates().pipe(catchError(() => of({data: []}))),
          projects: this.administrationFilterService.getProjects().pipe(catchError(() => of({data: []}))),
          services: this.administrationFilterService.getServices().pipe(catchError(() => of({data: []}))),
          serviceTypes: this.administrationFilterService.getServiceTypes().pipe(catchError(() => of({data: []}))),
          mbls: this.administrationFilterService.getMbls().pipe(catchError(() => of({data: []}))),
          containerRefs: this.administrationFilterService.getContainerReferences().pipe(catchError(() => of({data: []}))),
          suppliers: this.administrationFilterService.getSuppliers().pipe(catchError(() => of({data: []}))),
          clients: this.administrationFilterService.getClients().pipe(catchError(() => of({data: []}))),
          analyticOps: this.administrationFilterService.getAnalyticOperations().pipe(catchError(() => of({data: []}))),
          invoiceOrders: this.administrationFilterService.getInvoiceOrders().pipe(catchError(() => of({data: []}))),
          purchaseOrders: this.administrationFilterService.getPurchaseOrders().pipe(catchError(() => of({data: []}))),
        })
      );

      this.resetFilter();

      this.populateStateFilter(responses.states.data);
      this.populateCheckboxFilter('Proyecto', responses.projects.data, 'Service.Project.Id', (item) => item.name || item.reference || '-');
      this.populateCheckboxFilter('Servicio', responses.services.data, 'Service.Id', (item) => item.name || '-');
      this.populateCheckboxFilter('Tipo de servicio', responses.serviceTypes.data, 'Service.Type.Id', (item) => item.name || '-');
      this.populateCheckboxFilter('MBL', responses.mbls.data, 'MBL', (item) => (item.value || '').toString());
      this.populateCheckboxFilter('Referencia de contenedor', responses.containerRefs.data, 'ProjectContainer.ContainerRef', (item) => (item.value || '').toString());
      this.populateCheckboxFilter('Proveedor', responses.suppliers.data, 'Service.Supplier.Id', (item) => item.name || '-');
      this.populateCheckboxFilter('Cliente', responses.clients.data, 'Service.Project.Partner.Id', (item) => item.name || '-');
      this.populateCheckboxFilter('Operaciones analíticas', responses.analyticOps.data, 'AnalyticOperations.Id', (item) => item.description || '-');
      this.populateCheckboxFilter('Estados de pago', responses.invoiceOrders.data, 'AnalyticOperations.InvoiceOrderId', (item) => item.reference || '-');
      this.populateCheckboxFilter('Órdenes de compra', responses.purchaseOrders.data, 'AnalyticOperations.PurchaseOrderId', (item) => item.reference || '-');

      this.filters.forEach(fg => {
        fg.filtersActives = 0;
        fg.shownFiltersItems = [...fg.advancedFiltersItems];
      });

      this.mergeFilters(false);

      if (!this.storageFilters.length) {
        this.suppressFilterTrigger = true;
        this.storageFilters = this.cloneFilters(this.filters);
        this.suppressFilterTrigger = false;
      }

      this.setActiveFilters();
    } catch (error) {
      console.error('Error al cargar filtros de administración:', error);
    }
  }

  private populateStateFilter(data: AdministrationFilterOption[]): void {
    const filter = this.findFilterByName('Estado');
    if (!filter) {
      return;
    }

    const groupedStates = new Map<string, number[]>();
    data.forEach(item => {
      const name = item.name || '-';
      const rawValue = item.value ?? item.id;
      const numericValue = Number(rawValue);
      if (Number.isNaN(numericValue)) {
        return;
      }
      const list = groupedStates.get(name) ?? [];
      if (!list.includes(numericValue)) {
        list.push(numericValue);
      }
      groupedStates.set(name, list);
    });

    filter.advancedFiltersItems = [];
    groupedStates.forEach((ids, name) => {
      filter.advancedFiltersItems.push({
        label: name,
        value: {name, sequence: 0},
        idGroup: ids,
        isActive: false,
        type: 'event'
      });
    });

    filter.filtersActives = 0;
    filter.shownFiltersItems = [...filter.advancedFiltersItems];
  }

  private populateCheckboxFilter(
    filterName: string,
    data: AdministrationFilterOption[],
    path: string,
    labelFactory: (option: AdministrationFilterOption) => string,
  ): void {
    const filter = this.findFilterByName(filterName);
    if (!filter) {
      return;
    }

    const seen = new Set<string | number>();
    filter.advancedFiltersItems = [];

    data.forEach(item => {
      const value = item.id ?? item.value;
      if (value === undefined || value === null) {
        return;
      }

      const uniqueKey = `${path}-${value}`;
      if (seen.has(uniqueKey)) {
        return;
      }
      seen.add(uniqueKey);

      const label = labelFactory(item) || '-';
      filter.advancedFiltersItems.push({
        label,
        value,
        path,
        type: 'checkbox',
        isActive: false,
      });
    });

    filter.filtersActives = 0;
    filter.shownFiltersItems = [...filter.advancedFiltersItems];
  }

  private findFilterByName(name: string): AdvancedFilterGroup | undefined {
    return this.filters.find(f => f.name === name);
  }

  private cloneFilters(filters: AdvancedFilterGroup[]): AdvancedFilterGroup[] {
    return JSON.parse(JSON.stringify(filters));
  }

  private createEqualsCondition(property: string, value: any, collectionGroup?: string): FilterGroup {
    const condition: FilterGroup = {
      property,
      value: this.toFilterValue(value),
      operator: 'Equals'
    };

    if (collectionGroup) {
      condition.collectionGroup = collectionGroup;
    }

    return condition;
  }

  private toFilterValue(value: any): string | number | boolean | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    return String(value);
  }

  private cloneCondition(condition: FilterGroup): FilterGroup {
    return {
      property: condition.property,
      value: condition.value,
      operator: condition.operator,
      collectionGroup: condition.collectionGroup,
    };
  }

  _storageFilters: AdvancedFilterGroup[] | undefined;

  get storageFilters(): AdvancedFilterGroup[] {
    // Siempre leer directamente de localStorage para consistencia
    const stored = localStorage.getItem('advancedFilters');
    const parsed = stored ? JSON.parse(stored) : [];

    // Sincronizar con la propiedad interna
    if (!this._storageFilters || JSON.stringify(this._storageFilters) !== stored) {
      this._storageFilters = parsed;
    }

    return this._storageFilters || [];
  }

  set storageFilters(value: AdvancedFilterGroup[]) {
    const stringified = JSON.stringify(value);
    localStorage.setItem('advancedFilters', stringified);
    this._storageFilters = value;
    this.setActiveFilters();
    if (!this.suppressFilterTrigger) {
      this.filter();
    }
  }

  async getServiceOrders(options: {
    resetPage?: boolean;
    page?: number;
    skipPageAdjustment?: boolean;
  } = {}): Promise<void> {
    const {resetPage, page, skipPageAdjustment} = options;

    if (resetPage) {
      this.currentPage = 1;
    }

    if (page !== undefined) {
      this.currentPage = Math.max(1, page);
    }

    const paginationSnapshot = this.paginationData$.value;
    const pageSize = paginationSnapshot.pageSize || 10;
    const startRow = (this.currentPage - 1) * pageSize;
    const endRow = startRow + pageSize;

    await this.ensureFiltersInitialized();

    const filterGroups = this.buildFilterGroups();
    this.currentFilterGroups = filterGroups;

    const params: GetAdvancedParams = {
      startRow,
      endRow,
      sortProperty: this.currentSortProperty,
      sortDirection: this.currentSortDirection,
      filterGroups: filterGroups.length ? filterGroups : undefined,
    };

    this.tableLoading$.next(true);

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.stServiceService.getAdvancedServiceOrderForAdmin(params)
          .pipe(finalize(() => this.tableLoading$.next(false)))
      );

      const rows = (rsp.data?.rows || []).filter(so => !!so.project && !!so.service);
      rows.forEach(so => this.setOrderAndMoneyData(so));

      const totalItems = rsp.data?.lastRow ?? rows.length;
      const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
      const effectiveTotalPages = totalPages > 0 ? totalPages : (totalItems > 0 ? 1 : 0);

      if (!skipPageAdjustment && totalItems > 0 && rows.length === 0 && this.currentPage > 1) {
        const targetPage = effectiveTotalPages > 0 ? effectiveTotalPages : 1;
        this.currentPage = targetPage;
        await this.getServiceOrders({
          page: targetPage,
          skipPageAdjustment: true
        });
        return;
      }

      this.totalServiceOrders = rows;
      this.filteredServiceOrders$.next(rows);

      if (effectiveTotalPages > 0 && this.currentPage > effectiveTotalPages) {
        this.currentPage = effectiveTotalPages;
      }

      if (totalItems === 0) {
        this.currentPage = 1;
      }

      const computedStartRow = totalItems > 0 ? (this.currentPage - 1) * pageSize : 0;
      const computedEndRow = totalItems > 0 ? Math.min(computedStartRow + pageSize, totalItems) : 0;

      this.paginationData$.next({
        startRow: computedStartRow,
        endRow: computedEndRow,
        currentPage: this.currentPage,
        totalPages,
        totalItems,
        pageSize,
      });

      this.setActiveFilters();
    } catch (e) {
      console.error('Error al obtener ServiceOrders:', e);
      this.tableLoading$.next(false);
    }
  }

  updateLocalData(_: ServiceOrder[]): void {
    this.getServiceOrders({page: this.currentPage});
  }

  setPageSize(pageSize: number, reload: boolean): void {
    const sanitizedPageSize = Math.max(1, pageSize);
    const paginationSnapshot = this.paginationData$.value;

    if (paginationSnapshot.pageSize === sanitizedPageSize) {
      return;
    }

    this.paginationData$.next({
      ...paginationSnapshot,
      pageSize: sanitizedPageSize,
      currentPage: 1,
      startRow: 0,
      endRow: 0,
    });

    this.currentPage = 1;

    if (reload) {
      this.getServiceOrders({resetPage: true});
    }
  }

  setCurrentPage(page: number): void {
    this.currentPage = Math.max(1, page);
  }

  async updateSort(sortProperty: string, sortDirection: ServiceOrderSortDirection): Promise<void> {
    this.currentSortProperty = sortProperty;
    this.currentSortDirection = sortDirection;
    await this.getServiceOrders({resetPage: true});
  }

  async resetSort(): Promise<void> {
    this.currentSortProperty = this.defaultSortProperty;
    this.currentSortDirection = this.defaultSortDirection;
    await this.getServiceOrders({resetPage: true});
  }

  private buildFilterGroups(): FilterGroup[][] {
    const baseConditions: FilterGroup[] = [];
    const andConditions: FilterGroup[] = [];
    const orAlternatives: FilterGroup[][][] = [];

    const dateFilter = this.generalFilters[0];
    const dateFilterHasRange = dateFilter?.active
      && dateFilter.value?.startDate
      && dateFilter.value?.endDate;

    if (dateFilterHasRange) {
      baseConditions.push(
        {
          property: 'CreatedAt',
          value: dateFilter.value.endDate,
          operator: 'LessThanOrEqual'
        },
        {
          property: 'CreatedAt',
          value: dateFilter.value.startDate,
          operator: 'GreaterThanOrEqual'
        },
      );
    }

    const withoutInvoiceFilter = this.generalFilters.find(
      f => f.name === GENERAL_FILTER_LABELS.withoutInvoice
    );
    if (withoutInvoiceFilter?.active) {
      const collectionGroup = 'aoWithoutInvoice';
      andConditions.push(
        this.createEqualsCondition('AnalyticOperations.Class', 'Engaged', collectionGroup),
        this.createEqualsCondition('AnalyticOperations.Type', 'Ingreso', collectionGroup),
        this.createEqualsCondition('AnalyticOperations.InvoiceOrderId', null, collectionGroup),
      );
    }

    const withoutPurchaseOrderFilter = this.generalFilters.find(
      f => f.name === GENERAL_FILTER_LABELS.withoutPurchaseOrder
    );
    if (withoutPurchaseOrderFilter?.active) {
      const collectionGroup = 'aoWithoutPurchaseOrder';
      andConditions.push(
        this.createEqualsCondition('AnalyticOperations.Class', 'Engaged', collectionGroup),
        this.createEqualsCondition('AnalyticOperations.Type', 'Gasto', collectionGroup),
        this.createEqualsCondition('AnalyticOperations.PurchaseOrderId', null, collectionGroup),
      );
    }

    const searchValue = (this.searchValue || '').trim();
    if (searchValue) {
      const searchAlternatives = SEARCH_FILTER_FIELDS.map(property => ([
        {
          property,
          value: searchValue,
          operator: 'Contains'
        } as FilterGroup
      ]));
      if (searchAlternatives.length) {
        orAlternatives.push(searchAlternatives);
      }
    }

    this.filters
      .filter(fg => fg.filtersActives > 0)
      .forEach(fg => {
        const activeItems = fg.advancedFiltersItems.filter(item => item.isActive);
        if (!activeItems.length) {
          return;
        }

        switch (activeItems[0].type) {
          case 'checkbox': {
            const checkboxItems = activeItems as CheckboxFilterItem[];
            if (checkboxItems.length === 1) {
              const item = checkboxItems[0];
              andConditions.push(this.createEqualsCondition(item.path, item.value));
            } else {
              const alternatives = checkboxItems.map(item => [
                this.createEqualsCondition(item.path, item.value)
              ]);
              if (alternatives.length) {
                orAlternatives.push(alternatives);
              }
            }
            break;
          }
          case 'event': {
            const alternatives: FilterGroup[][] = [];
            activeItems.forEach(item => {
              const ids = (item as any).idGroup as number[] | undefined;
              if (ids && ids.length) {
                ids.forEach(id => {
                  alternatives.push([this.createEqualsCondition('State', id)]);
                });
              }
            });

            if (alternatives.length === 1) {
              andConditions.push(...alternatives[0]);
            } else if (alternatives.length > 1) {
              orAlternatives.push(alternatives);
            }
            break;
          }
          default:
            // Ignorar sliders u otros tipos por ahora
            break;
        }
      });

    const baseAndConditions = [...baseConditions, ...andConditions];
    const filterGroups: FilterGroup[][] = [];

    if (orAlternatives.length === 0) {
      if (baseAndConditions.length === 0) {
        return [];
      }
      filterGroups.push(baseAndConditions.map(condition => this.cloneCondition(condition)));
      return filterGroups;
    }

    let combinations: FilterGroup[][] = [[]];
    orAlternatives.forEach(alternatives => {
      const nextCombinations: FilterGroup[][] = [];
      combinations.forEach(existing => {
        alternatives.forEach(option => {
          nextCombinations.push([
            ...existing.map(condition => this.cloneCondition(condition)),
            ...option.map(condition => this.cloneCondition(condition)),
          ]);
        });
      });
      combinations = nextCombinations;
    });

    combinations.forEach(combo => {
      const combined = [
        ...combo,
        ...baseAndConditions.map(condition => this.cloneCondition(condition))
      ];
      filterGroups.push(combined);
    });

    return filterGroups;
  }

  async fetchServiceOrderByReference(reference: string): Promise<ServiceOrder | null> {
    if (!reference) {
      return null;
    }

    const params: GetAdvancedParams = {
      startRow: 0,
      endRow: 1,
      sortProperty: this.defaultSortProperty,
      sortDirection: this.defaultSortDirection,
      filterGroups: [
        [
          {
            property: 'Reference',
            value: reference,
            operator: 'Equals'
          }
        ]
      ],
    };

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.stServiceService.getAdvancedServiceOrderForAdmin(params)
      );

      const row = rsp.data?.rows?.[0];
      if (!row) {
        return null;
      }

      this.setOrderAndMoneyData(row);
      return row;
    } catch (error) {
      console.error('Error al obtener ServiceOrder por referencia:', error);
      return null;
    }
  }

  setOrderAndMoneyData(serviceOrder: ServiceOrder) {
    serviceOrder.hasIngreso=false
    serviceOrder.hasGasto=false
    serviceOrder.analyticOperations?.forEach(ao => {
      if (ao.class === 'Engaged') {
        if (ao.type === 'Ingreso' && !ao.invoiceOrderId) {
          serviceOrder.hasIngreso = true;
        }

        if (ao.type === 'Gasto' && !ao.purchaseOrderId) {
          serviceOrder.hasGasto = true;
        }
      }

      ao.currency = this.gs.currencies$.value?.find(c => c.id === ao.currencyId);
    });

    serviceOrder.moneyData = this.ps.processServiceOrderForMoneyData(serviceOrder);
  }

  resetFilter() {
    this.filters = [
      {
        name: 'Estado',
        advancedFiltersItems: [], // Estados de los Services involucrados *** Hacer logica especial***
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Proyecto',
        advancedFiltersItems: [], // Proyectos involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Servicio',
        advancedFiltersItems: [], // Servicios involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Tipo de servicio',
        advancedFiltersItems: [], // Tipos de servicio de los Services involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'MBL',
        advancedFiltersItems: [], // Tipos de servicio de los Services involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Referencia de contenedor',
        advancedFiltersItems: [], // Tipos de servicio de los Services involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Proveedor',
        advancedFiltersItems: [], // Partners involucrados como Proveedores (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Cliente',
        advancedFiltersItems: [], // Partners involucrados como Clientes (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Operaciones analíticas',
        advancedFiltersItems: [], // Operaciones analíticas involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Estados de pago',
        advancedFiltersItems: [], // Estados de pago involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Órdenes de compra',
        advancedFiltersItems: [], // Estados de pago involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
    ];
  }

  // Se unen los filtros guardados con las opciones actualizadas
  mergeFilters(updateStorage: boolean = true) {
    // Se recorre la lista de FilterGroups con las opciones actualizadas
    this.filters.forEach(fg => {

      // Se revisa si este FilterGroup ya existia y si tiene algun filtro activo
      const currentMatchFG = this.storageFilters
        .find(storageFilterGroup => storageFilterGroup.name === fg.name && storageFilterGroup.filtersActives > 0);

      // Si existe un FilterGroup con algun filtro activo se recorre la lista de FilterItems de las opciones actualizadas
      // junto con la lista de FilterItems de los filtros guardados
      if (currentMatchFG) {
        fg.advancedFiltersItems.forEach(fi => {

          // Se revisa si el FilterItem ya existia en las opciones guardadas
          const currentMatchFI = currentMatchFG.advancedFiltersItems
            .find(storageFilterItem => storageFilterItem.label === fi.label && (storageFilterItem.type === 'checkbox' ? (storageFilterItem.value === fi.value) : true));

          // Si existia se actualizan los valores y el estado de activo
          if (currentMatchFI && currentMatchFI.isActive) {
            fi.isActive = true;

            fg.filtersActives++;

            if (fi.type === 'slider' && currentMatchFI.type === 'slider') {
              const minValue = Math.max(currentMatchFI.value[0], fi.limits.min);
              const maxValue = Math.min(currentMatchFI.value[1], fi.limits.max);
              fi.value = [Math.min(minValue, maxValue), Math.max(maxValue, minValue)];
            }
          }
        })
      }
    });

    if (updateStorage && !this.storageFilters.length) this.storageFilters = [...this.filters];
  }

  setActiveFilters() {
    const advancedFiltersCount = this.filters.reduce((total, group) => {
      return total + (group.filtersActives || 0);
    }, 0);

    const generalFiltersCount = this.generalFilters.reduce((total, filter, index) => {
      if (!filter.active) {
        return total;
      }

      if (index === 0 && filter.name.includes(GENERAL_FILTER_LABELS.dateRange)) {
        return total + 1;
      }

      if (
        filter.name === GENERAL_FILTER_LABELS.withoutInvoice
        || filter.name === GENERAL_FILTER_LABELS.withoutPurchaseOrder
      ) {
        return total + 1;
      }

      return total;
    }, 0);

    this.activeFilters$.next(advancedFiltersCount + generalFiltersCount);
  }

  filter(resetPage: boolean = true): Promise<void> {
    return this.getServiceOrders({resetPage});
  }

}

